// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import { Button, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@stanion/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = WidgetProps;

export interface CreateAccountStyles {
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    step1Wrapper?: InjectableCss;
    tooltipWrapper?: InjectableCss;
    infoTipIconStyle?: InjectableCss;
    nextButtonWrapperStyles?: InjectableCss;
    navigationLink?: LinkPresentationProps;
    optionWrapper?: InjectableCss;
    radioGroup?: RadioGroupPresentationProps;
    labelRadio?: TypographyProps;
    infoText?: TypographyProps;
    radioWrapper?: InjectableCss;
    radioStyle?: InjectableCss;
}

export const createAccountStyles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    infoTipIconStyle: {
        css: css`
            margin-left: 5px;
        `,
    },
    nextButtonWrapperStyles: {
        css: css`
            margin-top: 15px;

            ${xScreen("lte", "xs")} {
                padding: 0 16px;
            }
        `,
    },
    navigationLink: {
        icon: { iconProps: { src: ChevronLeft } },
        css: css`
            margin-bottom: 20px;
        `,
    },
    optionWrapper: {
        css: css`
            margin-bottom: 16px;
            &:last-child {
                margin-bottom: 0;
            }
            ${xScreen("lte", "xs")} {
                padding: 0 16px;
            }
        `,
    },
    radioGroup: {
        css: css``,
    },
    labelRadio: {
        variant: "p",
        css: css`
            font-size: 16px;
            font-weight: bold;
            line-height: 1.2;
            margin-bottom: 0;
        `,
    },
    infoText: {
        size: 12,
        lineHeight: "17px",
        css: css`
            display: block;
        `,
    },
    radioWrapper: {
        css: css`
            display: flex;
        `,
    },
    radioStyle: {
        css: css`
            & input + label {
                align-items: flex-start;
                &:before,
                &:after {
                    margin-top: 3px;
                }
            }
        `,
    },
};

const styles = createAccountStyles;

const CreateAccount = ({ id }: Props) => {
    const [value, setValue] = useState();
    const [endFirstStep, setEndFirstStep] = useState(false);

    const createAnAccountHandler = (e: any) => {
        setValue(e.target.value);
    };

    const goToNextStepHandler = () => {
        if (value) {
            setEndFirstStep(true);
        }
    };

    const options: any = [
        {
            value: "existingAccount",
            labelMessage: siteMessage("Custom_AccountRegistration_Text_Option_1"),
            infoMessage: siteMessage("Custom_AccountRegistration_TextInfo_Option_1"),
        },
        {
            value: "creditApplication",
            labelMessage: siteMessage("Custom_AccountRegistration_Text_Option_2"),
            infoMessage: siteMessage("Custom_AccountRegistration_TextInfo_Option_2"),
        },
    ];

    const isOptionSelected = value && value !== null && value !== "";

    return (
        <>
            {!endFirstStep && (
                <StyledWrapper {...styles.step1Wrapper}>
                    <RadioGroup value={value} onChangeHandler={createAnAccountHandler} {...styles.radioGroup}>
                        {options.map((option: any) => {
                            return (
                                <StyledWrapper key={option.value} {...styles.optionWrapper}>
                                    <StyledWrapper {...styles.radioWrapper}>
                                        <Radio value={option.value} {...styles.radioStyle}>
                                            <Typography {...styles.labelRadio}>{option.labelMessage}</Typography>
                                        </Radio>
                                    </StyledWrapper>
                                    {option.infoMessage.toString().length > 0 && (
                                        <Typography {...styles.infoText}>{option.infoMessage}</Typography>
                                    )}
                                </StyledWrapper>
                            );
                        })}
                    </RadioGroup>
                    <StyledWrapper {...styles.nextButtonWrapperStyles}>
                        <Button onClick={goToNextStepHandler} disabled={!isOptionSelected}>{translate("Continue")}</Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}

            {endFirstStep && (
                <Link
                    {...styles.navigationLink}
                    onClick={() => {
                        setEndFirstStep(false);
                    }}
                >
                    {translate("Back to Previous Page")}
                </Link>
            )}

            {endFirstStep && value === "existingAccount" && (
                <StyledWrapper>
                    {createWidgetElement("CustomCreateAccount/CreateExistingAccount", {
                        fields: {},
                    })}
                </StyledWrapper>
            )}
            {endFirstStep && value === "creditApplication" && (
                <StyledWrapper>
                    <Zone contentId={id} zoneName="creditApplication" />
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CreateAccount,
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
